import { FormikProps } from 'formik';
import { ICS2CodelistsMap } from 'store/codelists/code';
import { DeclarationTypes } from 'store/declarations/enums/common/declaration-types';
import addPathPrefix from 'utils/addPathPrefix';
import DeclarationInput from 'components/ui/composed/declarations/formInput/DeclarationInput';
import DeclarationSelect from 'components/ui/composed/declarations/formSelect';
import useICS2Codelists from 'hooks/useICS2Codelists';
import { FormCardContainer } from 'views/declarations/common/cards/NewFormCard';
import MultipleItemsCard from 'views/declarations/common/MultipleItemsCard';
import { getFormikProps } from 'views/declarations/utils/form-utils';

/**
 * The cards every ICS2 ENS level renders the same way. The master level, a house consignment and a
 * goods item all carry these under a path of their own, so each card takes the prefix its level
 * sits under - the form root when it is left out - and is hidden where the message does not carry
 * it.
 */

type SharedCardProps = {
    formik: FormikProps<any>;
    path?: string;
    hidden?: boolean;
    viewOnly?: boolean;
};

export const SupportingDocumentsCard = ({ formik, path, hidden, viewOnly }: SharedCardProps) => (
    <MultipleItemsCard
        title="Supporting Documents"
        hidden={hidden}
        viewOnly={viewOnly}
        path={addPathPrefix(path, 'supportingDocuments')}
        initialValues={{ referenceNumber: '', type: undefined }}
        list={(list) => [
            { field: 'Reference Number', value: list.referenceNumber },
            { field: 'Type', value: list.type },
        ]}
        condensed
    >
        {(itemPath) => (
            <FormCardContainer>
                <DeclarationInput
                    required
                    refNumber="12 05 001 000"
                    label="Reference Number"
                    condensed
                    {...getFormikProps(addPathPrefix(itemPath, 'referenceNumber'), formik)}
                />
                <DeclarationSelect
                    hideCodelistMenu
                    required
                    refNumber="12 05 002 000"
                    label="Type"
                    codeValidation={[]}
                    condensed
                    codelistProps={{
                        codelistName: ICS2CodelistsMap.CL013,
                        declarationType: DeclarationTypes.ICS2,
                    }}
                    {...getFormikProps(addPathPrefix(itemPath, 'type'), formik)}
                />
            </FormCardContainer>
        )}
    </MultipleItemsCard>
);

export const TransportEquipmentCard = ({ formik, path, hidden, viewOnly }: SharedCardProps) => {
    const { getICS2CodeDescription } = useICS2Codelists();

    return (
        <MultipleItemsCard
            title="Transport Equipment"
            hidden={hidden}
            viewOnly={viewOnly}
            path={addPathPrefix(path, 'transportEquipment')}
            condensed
            initialValues={{
                containerIdentificationNumber: '',
                containerSizeAndType: undefined,
                containerPackedStatus: undefined,
                containerSupplierType: undefined,
                seal: [],
            }}
            list={(list) => [
                { field: 'Container ID', value: list.containerIdentificationNumber },
                {
                    field: 'Container Size and Type Identification',
                    value: getICS2CodeDescription(list.containerSizeAndType, ICS2CodelistsMap.CL710),
                },
                {
                    field: 'Container Packed Status',
                    value: getICS2CodeDescription(list.containerPackedStatus, ICS2CodelistsMap.CL709),
                },
                {
                    field: 'Container Supplier Type Code',
                    value: getICS2CodeDescription(list.containerSupplierType, ICS2CodelistsMap.CL711),
                },
                {
                    field: 'Seals',
                    value: list.seal?.map((seal: { identifier?: string }) => seal.identifier).join(', '),
                },
            ]}
        >
            {(itemPath) => (
                <>
                    <FormCardContainer>
                        <DeclarationInput
                            required
                            condensed
                            refNumber="19 07 063 000"
                            label="Container ID"
                            {...getFormikProps(addPathPrefix(itemPath, 'containerIdentificationNumber'), formik)}
                        />
                        <DeclarationSelect
                            hideCodelistMenu
                            label="Container Size and Type Identification"
                            required
                            refNumber="19 07 064 000"
                            codeValidation={[]}
                            condensed
                            codelistProps={{
                                codelistName: ICS2CodelistsMap.CL710,
                                declarationType: DeclarationTypes.ICS2,
                            }}
                            {...getFormikProps(addPathPrefix(itemPath, 'containerSizeAndType'), formik)}
                        />
                        <DeclarationSelect
                            hideCodelistMenu
                            label="Container Packed Status"
                            required
                            refNumber="19 07 065 000"
                            codeValidation={[]}
                            condensed
                            codelistProps={{
                                codelistName: ICS2CodelistsMap.CL709,
                                declarationType: DeclarationTypes.ICS2,
                            }}
                            {...getFormikProps(addPathPrefix(itemPath, 'containerPackedStatus'), formik)}
                        />
                        <DeclarationSelect
                            hideCodelistMenu
                            label="Container Supplier Type Code"
                            required
                            refNumber="19 07 066 000"
                            codeValidation={[]}
                            condensed
                            codelistProps={{
                                codelistName: ICS2CodelistsMap.CL711,
                                declarationType: DeclarationTypes.ICS2,
                            }}
                            {...getFormikProps(addPathPrefix(itemPath, 'containerSupplierType'), formik)}
                        />
                    </FormCardContainer>

                    <MultipleItemsCard
                        key={addPathPrefix(itemPath, 'seal')}
                        condensed
                        section
                        viewOnly={viewOnly}
                        title="Seal"
                        path={addPathPrefix(itemPath, 'seal')}
                        initialValues={{ identifier: '' }}
                        list={(seal) => [{ field: 'Identifier', value: seal.identifier }]}
                    >
                        {(sealPath) => (
                            <FormCardContainer>
                                <DeclarationInput
                                    required
                                    condensed
                                    refNumber="19 10 015 000"
                                    label="Identifier"
                                    {...getFormikProps(addPathPrefix(sealPath, 'identifier'), formik)}
                                />
                            </FormCardContainer>
                        )}
                    </MultipleItemsCard>
                </>
            )}
        </MultipleItemsCard>
    );
};

export const AdditionalSupplyChainActorCard = ({ formik, path, hidden, viewOnly }: SharedCardProps) => {
    const { getICS2CodeDescription } = useICS2Codelists();

    return (
        <MultipleItemsCard
            title="Additional Supply Chain Actor"
            hidden={hidden}
            viewOnly={viewOnly}
            path={addPathPrefix(path, 'additionalSupplyChainActor')}
            initialValues={{ role: undefined, identificationNumber: '' }}
            list={(list) => [
                { field: 'Role', value: getICS2CodeDescription(list.role, ICS2CodelistsMap.CL704) },
                { field: 'ID', value: list.identificationNumber },
            ]}
            condensed
        >
            {(itemPath) => (
                <FormCardContainer>
                    <DeclarationSelect
                        required
                        hideCodelistMenu
                        label="Role"
                        refNumber="13 14 031 000"
                        codeValidation={[]}
                        condensed
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL704,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                        {...getFormikProps(addPathPrefix(itemPath, 'role'), formik)}
                    />
                    <DeclarationInput
                        required
                        condensed
                        label="ID"
                        refNumber="13 14 017 000"
                        {...getFormikProps(addPathPrefix(itemPath, 'identificationNumber'), formik)}
                    />
                </FormCardContainer>
            )}
        </MultipleItemsCard>
    );
};

export const AdditionalInformationCard = ({ formik, path, hidden, viewOnly }: SharedCardProps) => (
    <MultipleItemsCard
        title="Additional Information"
        hidden={hidden}
        viewOnly={viewOnly}
        path={addPathPrefix(path, 'additionalInformation')}
        initialValues={{ code: undefined, text: '' }}
        list={(list) => [
            { field: 'Code', value: list.code },
            { field: 'Text', value: list.text },
        ]}
        condensed
    >
        {(itemPath) => (
            <FormCardContainer>
                <DeclarationSelect
                    hideCodelistMenu
                    label="Code"
                    refNumber="12 02 008 000"
                    codeValidation={[]}
                    condensed
                    codelistProps={{
                        codelistName: ICS2CodelistsMap.CL701,
                        declarationType: DeclarationTypes.ICS2,
                    }}
                    {...getFormikProps(addPathPrefix(itemPath, 'code'), formik)}
                />
                <DeclarationInput
                    condensed
                    maxLength={512}
                    refNumber="12 02 009 000"
                    label="Text"
                    {...getFormikProps(addPathPrefix(itemPath, 'text'), formik)}
                />
            </FormCardContainer>
        )}
    </MultipleItemsCard>
);

export const PassiveBorderTransportMeansCard = ({
    formik,
    path,
    hidden,
    viewOnly,
    nationalityRequired = true,
}: SharedCardProps & {
    nationalityRequired?: boolean;
}) => {
    const { getICS2CodeDescription } = useICS2Codelists();

    return (
        <MultipleItemsCard
            title="Passive Border Transport Means"
            hidden={hidden}
            viewOnly={viewOnly}
            path={addPathPrefix(path, 'passiveBorderTransportMeans')}
            initialValues={{
                typeOfMeansOfTransport: undefined,
                typeOfIdentification: undefined,
                identificationNumber: '',
                nationality: undefined,
            }}
            list={(list) => [
                {
                    field: 'Type',
                    value: getICS2CodeDescription(list.typeOfMeansOfTransport, ICS2CodelistsMap.CL751),
                },
                {
                    field: 'ID Type',
                    value: getICS2CodeDescription(list.typeOfIdentification, ICS2CodelistsMap.CL750),
                },
                { field: 'ID', value: list.identificationNumber },
                { field: 'Nationality', value: getICS2CodeDescription(list.nationality, ICS2CodelistsMap.CL718) },
            ]}
            condensed
        >
            {(itemPath) => (
                <FormCardContainer>
                    <DeclarationSelect
                        required
                        hideCodelistMenu
                        label="Type"
                        refNumber="19 09 067 000"
                        codeValidation={[]}
                        condensed
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL751,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                        {...getFormikProps(addPathPrefix(itemPath, 'typeOfMeansOfTransport'), formik)}
                    />
                    <DeclarationSelect
                        required
                        hideCodelistMenu
                        label="ID Type"
                        refNumber="19 09 061 000"
                        codeValidation={[]}
                        condensed
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL750,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                        {...getFormikProps(addPathPrefix(itemPath, 'typeOfIdentification'), formik)}
                    />
                    <DeclarationInput
                        required
                        condensed
                        maxLength={35}
                        refNumber="19 08 017 000"
                        label="ID"
                        {...getFormikProps(addPathPrefix(itemPath, 'identificationNumber'), formik)}
                    />
                    <DeclarationSelect
                        required={nationalityRequired}
                        hideCodelistMenu
                        label="Nationality"
                        refNumber="19 09 062 000"
                        codeValidation={[]}
                        condensed
                        codelistProps={{
                            codelistName: ICS2CodelistsMap.CL718,
                            declarationType: DeclarationTypes.ICS2,
                        }}
                        {...getFormikProps(addPathPrefix(itemPath, 'nationality'), formik)}
                    />
                </FormCardContainer>
            )}
        </MultipleItemsCard>
    );
};

export const DangerousGoodsCard = ({ formik, path, hidden, viewOnly }: SharedCardProps) => (
    <MultipleItemsCard
        title="Dangerous Goods"
        hidden={hidden}
        viewOnly={viewOnly}
        path={addPathPrefix(path, 'commodity.dangerousGoods')}
        initialValues={{ unNumber: undefined }}
        list={(list) => [{ field: 'UN Number', value: list.unNumber }]}
        condensed
    >
        {(itemPath) => (
            <FormCardContainer>
                <DeclarationSelect
                    required
                    hideCodelistMenu
                    label="UN Number"
                    refNumber="18 07 055 000"
                    codeValidation={[]}
                    condensed
                    codelistProps={{
                        codelistName: ICS2CodelistsMap.CL101,
                        declarationType: DeclarationTypes.ICS2,
                    }}
                    {...getFormikProps(addPathPrefix(itemPath, 'unNumber'), formik)}
                />
            </FormCardContainer>
        )}
    </MultipleItemsCard>
);
